/**
 * An approval the case agent asks for: approve (1) or decline (2), with a
 * note; Enter records. The budget ask takes the new threshold to raise it
 * to — a threshold a person raises, never a wall. Partner work is approved
 * in Setup, with the partner and the terms: here it only declines.
 */

import { useState } from 'react';

import { Button, Group, NumberInput, Stack, Text, TextInput } from '@mantine/core';

import type { Intervention, WorkbenchHeader } from '@/types/workbench';
import { formatUsd } from '../format';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { AskHeading, Kbd, listOf, useAnswer } from './common';
import classes from '../NeedsYou.module.css';

interface ApprovalPaneProps {
  header: WorkbenchHeader;
  ask: Intervention;
  subjectName: string | null;
  onAnswered: (ids: string[]) => void | Promise<void>;
  /** Open Setup, where partner work is approved. */
  onOpenSetup?: () => void;
  keyboard: boolean;
}

/** A sensible step up from the threshold the case runs under. */
export function suggestedBudget(header: WorkbenchHeader): number {
  const current = Number(header.spend.threshold.usd ?? 0);
  const spent = Number(header.spend.threshold.spent);
  const base = Math.max(current, spent);
  return Math.ceil((base * 1.5) / 5) * 5 || 10;
}

export function ApprovalPane({
  header,
  ask,
  subjectName,
  onAnswered,
  onOpenSetup,
  keyboard,
}: Readonly<ApprovalPaneProps>) {
  const { submitting, submit } = useAnswer(header.reportId, onAnswered);
  const [decision, setDecision] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [budget, setBudget] = useState<number | string>(suggestedBudget(header));
  const budgetAsk = ask.target.type === 'budget';
  const partnerWork = ask.target.type === 'partner_work';
  const components = listOf(ask.payload.components);
  const budgetValue = Number(budget);
  const ready =
    decision !== null &&
    (!decision || !budgetAsk || (Number.isFinite(budgetValue) && budgetValue > 0)) &&
    !(decision && partnerWork);

  const record = () => {
    if (!ready || decision === null || submitting) {
      return;
    }
    void submit(ask, {
      approved: decision,
      ...(decision && budgetAsk ? { budget_usd: budgetValue } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };
  useWorkbenchKeys(
    {
      '1': () => setDecision(true),
      '2': () => setDecision(false),
      Enter: record,
    },
    keyboard
  );

  const { threshold } = header.spend;
  return (
    <>
      <div className={classes.detailBody} data-testid="pane-approval">
        <Stack gap="sm">
          <AskHeading
            section={ask.section}
            subjectName={subjectName}
            question={ask.question}
            why={ask.reason}
            blocking={ask.waitingTaskId !== null}
          />
          {budgetAsk && (
            <Text size="xs" c="dimmed" data-testid="approval-threshold">
              Spent {formatUsd(threshold.spent)} of{' '}
              {threshold.usd === null ? 'no threshold' : formatUsd(threshold.usd)} (
              {threshold.scope === 'before_start' ? 'the budget before Start' : 'the case budget'}
              ). Raising it lets the runs carry on; declining stops here until you do.
            </Text>
          )}
          {partnerWork && (
            <Stack gap={4} data-testid="approval-partner-work">
              {components.length > 0 && (
                <Text size="xs" c="dimmed">
                  Work: {components.join(', ')}
                  {typeof ask.target.jurisdiction === 'string'
                    ? ` · in ${ask.target.jurisdiction}`
                    : ''}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                Partner work is approved in Setup, with the partner and the terms; decline it here
                if no partner should do it.
              </Text>
              {onOpenSetup && (
                <Button
                  size="xs"
                  variant="default"
                  onClick={onOpenSetup}
                  style={{ alignSelf: 'flex-start' }}
                  data-testid="approval-open-setup"
                >
                  Open Setup
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </div>
      <Stack gap={6} className={classes.answers} data-testid={`approval-bar-${ask.id}`}>
        <Group gap={6} wrap="wrap" align="flex-end">
          {!partnerWork && (
            <Button
              size="xs"
              variant={decision === true ? 'filled' : 'default'}
              color="teal"
              onClick={() => setDecision(true)}
              aria-pressed={decision === true}
              data-testid="approval-approve"
            >
              {budgetAsk ? 'Raise it' : 'Approve'}
              <Kbd>1</Kbd>
            </Button>
          )}
          <Button
            size="xs"
            variant={decision === false ? 'filled' : 'default'}
            color="red"
            onClick={() => setDecision(false)}
            aria-pressed={decision === false}
            data-testid="approval-decline"
          >
            {budgetAsk ? 'Stop here' : partnerWork ? 'No partner for this' : "Don't approve"}
            <Kbd>2</Kbd>
          </Button>
          {budgetAsk && decision === true && (
            <NumberInput
              size="xs"
              w={140}
              label="New threshold, $"
              min={1}
              step={5}
              value={budget}
              onChange={setBudget}
              data-testid="approval-budget"
            />
          )}
        </Group>
        <Group gap={6} wrap="nowrap">
          <TextInput
            size="xs"
            placeholder="A note (optional)"
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            size="xs"
            onClick={record}
            disabled={!ready}
            loading={submitting}
            data-testid="approval-record"
          >
            Record
            <Kbd>Enter</Kbd>
          </Button>
        </Group>
      </Stack>
    </>
  );
}
